import { Accordion, AccordionSummary, Typography, AccordionDetails, Box, styled } from '@mui/material';
import { ExpandMore, Download, Upload } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAppContext } from '@/provider/contexts';
import { uploadAssetListAPI } from '@/api/admin/setting';
import BaseButton from '@/components/ui/BaseButton';
import { ChangeEvent, useMemo, useState } from 'react';
import saveAs from 'file-saver';
import dayjs from 'dayjs';
import { StoreInfo } from '@/types/pages/stores';

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const AssetListAccordion = ({ storeInfo, assetList }: { storeInfo: StoreInfo; assetList: string[] }) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const [loading, setLoading] = useState(false);
    const [allowedDeviceMacs, setAllowedDeviceMacs] = useState(assetList);

    const fileNameForExport = useMemo(() => {
        const exportDate = dayjs().format('YYYYMMDD');
        const exportTime = dayjs().format('HHmmss');
        return `aura_asset_list_${storeInfo.store_name}_${storeInfo.store_code}_${exportDate}_${exportTime}.txt`;
    }, [storeInfo.store_name, storeInfo.store_code]);

    const handleDownloadList = () => {
        if (allowedDeviceMacs.length === 0) {
            setNotification({
                message: t('No allowed devices found in this store.'),
                severity: 'info',
            });
            return;
        }

        const content = allowedDeviceMacs.join('\n');
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        saveAs(blob, fileNameForExport);
    };

    const handleUploadList = async (event: ChangeEvent<HTMLInputElement>) => {
        const inputEl = event.target;
        const file = inputEl.files?.[0];

        inputEl.value = '';

        if (!file) return;

        const ext = file.name.split('.').pop()?.toLowerCase();
        if (ext !== 'txt') {
            setNotification({ message: t('Only .txt files are supported.'), severity: 'error' });
            return;
        }

        // if (file.size > MAX_TXT_SIZE_BYTES) {
        //     setNotification({ message: t('File is too large.'), severity: 'error' });
        //     return;
        // }

        try {
            const rawText = await file.text();

            const macList = Array.from(
                new Set(
                    rawText
                        .replace(/^\uFEFF/, '')
                        .split(/\r?\n/)
                        .map((line) => line.trim())
                        .filter(Boolean),
                ),
            );

            if (macList.length === 0) {
                setNotification({ message: t('Uploaded file is empty.'), severity: 'error' });
                return;
            }
            setLoading(true);
            const {
                data: { data, isSuccess, message },
            } = await uploadAssetListAPI(manageStoreId as number, macList);

            if (!isSuccess) {
                throw new Error(message);
            }

            setAllowedDeviceMacs([...(data.existed_entries || []), ...(data.added_entries || [])]);
            setNotification({ message: t('Update successful!'), severity: 'success' });
        } catch (error: unknown) {
            if (error instanceof Error) {
                setNotification({ message: error.message || t('Failed to Upload'), severity: 'error' });
                return;
            }
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle1">{t('Asset List')}</Typography>
            </AccordionSummary>

            <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        bgcolor: 'grey.100',
                        borderRadius: 1,
                        px: 2,
                        py: 1.5,
                    }}
                >
                    <Typography variant="body2" fontWeight={700}>
                        {t(
                            'Asset List refers to a collection of MAC addresses that represent device listed in the store.',
                        )}
                    </Typography>
                    <Typography variant="body2">
                        {t('Current allowed devices in this store: {{count}}.', { count: allowedDeviceMacs.length })}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1.5, mt: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    <BaseButton
                        variant="contained"
                        color="success"
                        startIcon={<Download />}
                        onClick={handleDownloadList}
                    >
                        {t('Download Allowed Device List')}
                    </BaseButton>

                    <BaseButton
                        component="label"
                        variant="contained"
                        color="primary"
                        startIcon={<Upload />}
                        loading={loading}
                    >
                        {t('Replace Allowed Device List')}
                        <VisuallyHiddenInput type="file" accept=".txt" onChange={handleUploadList} />
                    </BaseButton>
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};

export default AssetListAccordion;
